import { CLARITY_DOMAIN_ROUTE_SEGMENT } from './constants/clarity-workspace-route.constant';
import type { ClarityWorkspaceNavigation } from './models/clarity-workspace-navigation.model';

const PATH_SEPARATOR = '/';
const QUERY_MARK = '?';
const FRAGMENT_MARK = '#';

/**
 * A URL as Clarity's `useWorkspaceRoute` reads it — the workspace route's params (`useParams`) and the
 * query (`location.search`) — plus the decoded path the no-op check compares.
 */
export interface ClarityWorkspaceRoute {
	/**
	 * `domainId` and `tab` when the URL is a workspace URL (`/<section>/d/<domainId>[/<tab>…]`), else
	 * nothing — what brain's `/d/:domainId/:tab?` route hands `useParams`.
	 */
	readonly params: Readonly<Record<string, string | undefined>>;
	/** The query, in the order the URL carries it. */
	readonly search: URLSearchParams;
	/** Whether the URL is a workspace URL at all. */
	readonly isWorkspaceUrl: boolean;
	/** The path with each segment decoded, fragment and query left out. */
	readonly pathname: string;
}

function decodeSegment(segment: string): string {
	try {
		return decodeURIComponent(segment);
	} catch {
		return segment;
	}
}

/** A URL cut at its query mark, the fragment dropped. */
interface SplitUrl {
	readonly path: string;
	readonly query: string;
}

function splitUrl(url: string): SplitUrl {
	const [beforeFragment = ''] = url.split(FRAGMENT_MARK);
	const queryStart = beforeFragment.indexOf(QUERY_MARK);

	return queryStart === -1
		? { path: beforeFragment, query: '' }
		: { path: beforeFragment.slice(0, queryStart), query: beforeFragment.slice(queryStart + 1) };
}

/** Ported from Clarity `apps/web/src/shared/hooks/useWorkspaceRoute.ts` (commit `f9217bb5a`). */
/**
 * Reads a root-absolute URL — the router's current one — the way brain's `useWorkspaceRoute` reads
 * `useParams` and `location.search` (`currentSearchAndUrl`, `currentParsed`).
 */
export function readClarityWorkspaceRoute(url: string, sectionPath: string): ClarityWorkspaceRoute {
	const { path, query } = splitUrl(url);
	const segments = path
		.split(PATH_SEPARATOR)
		.filter(segment => segment !== '')
		.map(decodeSegment);
	const [section, domainSegment, domainId, tab] = segments;
	const isWorkspaceUrl =
		section === sectionPath && domainSegment === CLARITY_DOMAIN_ROUTE_SEGMENT && domainId !== undefined;

	return {
		params: isWorkspaceUrl ? { domainId, tab } : {},
		search: new URLSearchParams(query),
		isWorkspaceUrl,
		pathname: `${PATH_SEPARATOR}${segments.join(PATH_SEPARATOR)}`,
	};
}

/**
 * Appends one path segment to a URL ahead of its query — how the port hangs an open item under the Open
 * Items tab (`open-items/<oiSequence>`) where brain writes `?oi=`. The segment is percent-encoded, as the
 * builder encodes the domain id, and the fragment is dropped, as everywhere in this file.
 */
export function appendClarityPathSegment(url: string, segment: string): string {
	const { path, query } = splitUrl(url);
	const withSegment = `${path}${PATH_SEPARATOR}${encodeURIComponent(segment)}`;

	return query === '' ? withSegment : `${withSegment}${QUERY_MARK}${query}`;
}

/** Puts one of brain's own paths (`/d/…`, `/`, `/?…`) under the Clarity section. */
export function toClaritySectionUrl(sectionPath: string, brainUrl: string): string {
	const tail =
		brainUrl === PATH_SEPARATOR || brainUrl.startsWith(`${PATH_SEPARATOR}${QUERY_MARK}`)
			? brainUrl.slice(1)
			: brainUrl;

	return `${PATH_SEPARATOR}${sectionPath}${tail}`;
}

/**
 * The navigation to `url`, or `null` when `url` is where `current` already is — brain's TR-014: a URL
 * that would not change produces no navigation and no history entry.
 *
 * Both sides are compared after one serialisation, `URLSearchParams`', which is the one brain's builder
 * writes: the router serialises a query its own way (a space as `%20`, `:` kept), so comparing its URL
 * as a string would see a change on every call. The fragment is left out, as brain compares
 * `pathname + search` only.
 */
export function toNavigationUnlessCurrent(
	current: ClarityWorkspaceRoute,
	url: string,
	replaceUrl: boolean,
): ClarityWorkspaceNavigation | null {
	const { path, query } = splitUrl(url);
	const isCurrent = path === current.pathname && new URLSearchParams(query).toString() === current.search.toString();

	return isCurrent ? null : { url, replaceUrl };
}
